<script setup>
import { REVIEW_CATEGORIES, defaultsHint, reviewOn as reviewOnFor, settingsInForce, withReview } from '@/utils/reviewSettings'
import {useRestaurantsStore, useUserGroupsStore, useUsersStore} from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import moment from 'moment'
import axios from '@axios'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import ContactAppendsEditor from '@core/components/ContactAppendsEditor.vue'
import {generateGeneralReservationUrlWithQueryString, numberWithCommas} from '@/plugins/misc'
import { cleanContactEntries, parseContactEntries } from '@/plugins/contactEntries'
import {
  emailValidator,
  phoneValidator, profileIdValidator,
  requiredValidator,
  reservationPlatformUrlValidator
} from '@validators'
import { mediaUrl } from '@core/utils/mediaUrl'
import imagePlaceholder from '@/assets/images/imagePlaceholder.webp'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])
const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let adminEditingUser = null
let myUser = null;
({ myUser } = storeToRefs(usersStore))


const userGroupsStore = useUserGroupsStore()

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let groups = null
let markets = null
let timeZones = null
let adminEditingRestaurant = null;
({ groups, markets, restaurants, adminEditingRestaurant, timeZones  } = storeToRefs(restaurantsStore))

// RM-403: queue a read of this restaurant's own website (its reservation link and emails). The read happens in the
// background; what it finds is applied or shows in Admin Settings → Restaurant Data Review.
const websiteReadBusy = ref(false)
const websiteReadNote = ref('')
async function readWebsiteNow() {
  websiteReadBusy.value = true
  websiteReadNote.value = ''
  try {
    const res = await axios.post('/admin/restaurant-data-review/read', { restaurantId: adminEditingRestaurant.value._id })
    websiteReadNote.value = res?.data?.data?.queued === false
      ? 'This website is being read right now. What it finds shows in Restaurant Data Review.'
      : 'Queued. What it finds shows in Restaurant Data Review.'
  } catch (e) {
    websiteReadNote.value = e?.response?.data?.error || 'Could not queue that. Please try again.'
  } finally {
    websiteReadBusy.value = false
  }
}

// RM-359 — RM's review before a request is sent here, one switch per member
// category (platform admins only; the restaurant never sees them). The rule
// and its defaults live in utils/reviewSettings (same as lib/reservationReview).
const reviewSettingsInForce = computed(() => settingsInForce(adminEditingRestaurant.value))
const reviewOn = key => reviewOnFor(adminEditingRestaurant.value, key)
const reviewDefaultsHint = computed(() => defaultsHint(adminEditingRestaurant.value))

function setReview(key, value) {
  if (adminEditingRestaurant.value) adminEditingRestaurant.value.reviewSettings = withReview(adminEditingRestaurant.value, key, value)
}

function resetReview() {
  if (adminEditingRestaurant.value) adminEditingRestaurant.value.reviewSettings = null
}

// RM-224 — per-market neighborhood vocabulary. Mirrors NEIGHBORHOOD_VOCAB in
// lib/neighborhoodClassifier.js. Unlike cuisines the vocabulary is OPEN
// (combobox): boundary/renamed areas and uncurated markets take custom slugs.
const neighborhoodVocab = {
  'Chicago, IL': ['river-north', 'fulton-market', 'the-loop', 'west-loop', 'gold-coast', 'streeterville', 'old-town', 'lincoln-park', 'west-town', 'wicker-park', 'bucktown', 'logan-square', 'river-west', 'south-loop', 'lakeview', 'andersonville', 'lincoln-square', 'hyde-park', 'pilsen', 'ukrainian-village', 'greektown', 'chinatown', 'uptown'],
  'New York, NY': ['west-village', 'greenwich-village', 'east-village', 'soho', 'nolita', 'noho', 'tribeca', 'lower-east-side', 'chinatown', 'little-italy', 'financial-district', 'meatpacking-district', 'chelsea', 'flatiron', 'nomad', 'gramercy', 'union-square', 'murray-hill', 'koreatown', 'midtown', 'midtown-east', 'hells-kitchen', 'theater-district', 'hudson-yards', 'upper-east-side', 'upper-west-side', 'columbus-circle', 'harlem'],
  'Brooklyn, NY': ['williamsburg', 'greenpoint', 'dumbo', 'brooklyn-heights', 'cobble-hill', 'carroll-gardens', 'park-slope', 'fort-greene', 'prospect-heights', 'bushwick', 'bed-stuy', 'gowanus', 'red-hook'],
  'Miami, FL': ['brickell', 'downtown-miami', 'wynwood', 'design-district', 'edgewater', 'midtown-miami', 'coconut-grove', 'little-havana', 'little-haiti', 'overtown', 'key-biscayne', 'doral', 'watson-island'],
  'Miami Beach, FL': ['south-beach', 'mid-beach', 'north-beach', 'sunset-harbour'],
  'Las Vegas, NV': ['the-strip', 'downtown-las-vegas', 'arts-district', 'chinatown', 'off-strip', 'summerlin'],
  'Nashville, TN': ['downtown', 'the-gulch', 'germantown', 'midtown', 'music-row', 'east-nashville', '12-south', 'green-hills', 'wedgewood-houston', 'sylvan-park'],
  'Austin, TX': ['downtown', 'rainey-street', 'east-austin', 'south-congress', 'south-lamar', 'south-first', 'bouldin-creek', 'zilker', 'clarksville', 'hyde-park', 'the-domain', 'mueller'],
  'San Francisco, CA': ['financial-district', 'union-square', 'north-beach', 'chinatown', 'embarcadero', 'soma', 'mission', 'hayes-valley', 'marina', 'cow-hollow', 'pacific-heights', 'nob-hill', 'russian-hill', 'castro', 'dogpatch', 'japantown', 'fillmore', 'lower-haight'],
  'Houston, TX': ['montrose', 'river-oaks', 'downtown', 'midtown', 'heights', 'upper-kirby', 'galleria', 'rice-village', 'memorial', 'east-downtown', 'washington-corridor'],
  'Atlanta, GA': ['buckhead', 'midtown', 'downtown', 'westside', 'old-fourth-ward', 'inman-park', 'virginia-highland', 'poncey-highland', 'reynoldstown', 'east-atlanta'],
  'Washington, DC': ['georgetown', 'dupont-circle', 'logan-circle', 'shaw', 'penn-quarter', 'capitol-hill', 'navy-yard', 'adams-morgan', 'u-street', 'foggy-bottom', 'downtown', 'the-wharf', 'noma'],
  'Naples, FL': ['old-naples', 'north-naples', 'park-shore', 'mercato'],
  'Scottsdale, AZ': ['old-town-scottsdale', 'north-scottsdale', 'south-scottsdale', 'gainey-ranch', 'kierland', 'dc-ranch'],
  'Dallas, TX': ['uptown', 'downtown', 'deep-ellum', 'design-district', 'knox-henderson', 'bishop-arts', 'oak-lawn', 'park-cities', 'victory-park', 'lower-greenville'],
  'Los Angeles, CA': ['downtown-la', 'hollywood', 'los-feliz', 'silver-lake', 'echo-park', 'koreatown', 'fairfax', 'melrose', 'venice', 'brentwood', 'century-city', 'sawtelle', 'westwood', 'pacific-palisades', 'palms', 'culver-city'],
}

const neighborhoodItems = computed(() => {
  const vocab = neighborhoodVocab[adminEditingRestaurant.value?.market] || []
  const current = adminEditingRestaurant.value?.neighborhoods || []

  return [...new Set([...vocab, ...current])]
})

// Cuisine tag vocabulary — mirrors CUISINE_TAGS in lib/cuisineClassifier.js
// (the server filter only matches vocabulary tags, so the editor constrains to it).
const cuisineTags = [
  'american',
  'new-american',
  'steakhouse',
  'seafood',
  'southern',
  'cajun-creole',
  'bbq',
  'pizza',
  'italian',
  'french',
  'spanish',
  'portuguese',
  'greek',
  'mediterranean',
  'middle-eastern',
  'turkish',
  'israeli',
  'moroccan',
  'ethiopian',
  'indian',
  'thai',
  'vietnamese',
  'chinese',
  'korean',
  'japanese',
  'sushi',
  'filipino',
  'hawaiian',
  'mexican',
  'tex-mex',
  'latin-american',
  'peruvian',
  'brazilian',
  'argentinian',
  'caribbean',
  'cuban',
  'british',
  'german',
  'eastern-european',
  'scandinavian',
  'european',
  'fusion',
  'vegetarian-vegan',
  'breakfast-brunch',
  'bakery-cafe',
  'bar-lounge',
  'winery',
  'dessert',
  'other',
]

const isDialogVisible = ref(false)

// RM-205 (Kapil 9/26): next to each referral phone number, the person's
// texting status (from RM via Text → Restaurant team).
const teamCaptions = ref({})
const phoneDigits = v => String(v || '').replace(/\D/g, '').slice(-10)
async function loadTextingForRestaurant(restaurantId) {
  teamCaptions.value = {}
  if (!restaurantId || myUser.value?.userLevel !== 'admin') return
  try {
    const team = await axios.get('/agent/admin/sms-restaurant-team')
    const captions = {}
    for (const p of (team.data.people || [])) {
      const label = p.status === 'active' ? `Texting confirmed${p.textingEnabled ? '' : ' (switched off)'}`
        : p.status === 'invited' ? 'Invited - waiting for their tap'
          : p.status === 'optedOut' ? 'Opted out (texted STOP)'
            : 'Not invited yet - invite on RM via Text'
      captions[phoneDigits(p.phoneE164)] = label
    }
    teamCaptions.value = captions
  } catch (e) {
    teamCaptions.value = {}
  }
}
watch(() => adminEditingRestaurant.value?._id, id => loadTextingForRestaurant(id), { immediate: true })
const isConfirmDialogOpen = ref(false)
const isConfirmSmsDialogOpen = ref(false)

// RM-157: Google Place panel open state — collapsed when already linked,
// expanded (0) when not linked so admins are guided to link it.
const googlePlacePanel = ref(0)

// Photo uploads — parity with the restaurant-profile settings editor.
const choosingImages = ref(false)
const currentImage = ref(0)

const handleMediaSelection = selectedMedia => {
  adminEditingRestaurant.value.imageIds = selectedMedia || null
  choosingImages.value = false
}

// Booking-window guidance text (mirrors the restaurant-profile settings editor).
const bookingWindowGuidance = computed(() => {
  const raw = adminEditingRestaurant.value?.reservationBookingWindowDays
  const n = Number(raw)
  if (!raw || !Number.isInteger(n) || n <= 0) {
    return 'Members can request any date while their membership is active.'
  }
  const tz = adminEditingRestaurant.value?.timeZoneIdentifier || 'America/Chicago'
  const lastDate = moment.tz(tz).add(n - 1, 'days').format('ddd, MMM D')

  return `With ${n} days, members can request through ${lastDate} — today counts as day 1. The window slides forward each day.`
})

// RM-189 — phone appends are now [{ name, phone }]; track a NORMALIZED snapshot
// (JSON of canonical entries) so the "phones changed → confirm SMS" check below
// survives string↔array shape differences and array reference changes.
const normalizedAppends = (v, key) => JSON.stringify(parseContactEntries(v, key))
let originalGeneralReservationPhoneNumberAppends = null
watch(adminEditingRestaurant, newValue => {
  if(newValue){
    originalGeneralReservationPhoneNumberAppends = normalizedAppends(newValue.generalReservationPhoneNumberAppends, 'phone')

    // Default instantBookingTeamIdentity if empty
    if (!newValue.instantBookingTeamIdentity) {
      newValue.instantBookingTeamIdentity = `${newValue.name} Team`
    }

    // Default instantBookingEmailAppends to generalReservationEmailAppends if empty.
    // RM-189 — appends are arrays ([] is truthy), so guard on parsed length and
    // clone the entries so the two fields don't share one array reference.
    const generalEmailEntries = parseContactEntries(newValue.generalReservationEmailAppends, 'email')
    if (!parseContactEntries(newValue.instantBookingEmailAppends, 'email').length && generalEmailEntries.length) {
      newValue.instantBookingEmailAppends = generalEmailEntries.map(e => ({ ...e }))
    }

    // Initialize openingHours array to ensure it has exactly 7 days
    // First check if we need to extract from googlePlaceDetails
    if (!newValue.openingHours || newValue.openingHours.length === 0) {
      // Try to get from Google Place Details if available
      if (newValue.googlePlaceDetails?.opening_hours?.weekday_text && 
          newValue.googlePlaceDetails.opening_hours.weekday_text.length > 0) {
        newValue.openingHours = [...newValue.googlePlaceDetails.opening_hours.weekday_text]
      } else {
        newValue.openingHours = Array(7).fill('')
      }
    } else if (newValue.openingHours.length < 7) {
      // Pad array to 7 days if it has less
      const existing = [...newValue.openingHours]
      newValue.openingHours = Array(7).fill('').map((_, i) => existing[i] || '')
    } else if (newValue.openingHours.length > 7) {
      // Trim to 7 days if it has more
      newValue.openingHours = newValue.openingHours.slice(0, 7)
    }
  }
}, { deep: true })

const form = ref()
const valid = ref(true)
const changesMade = ref(true)

// Form Errors
const errors = ref({
  profileId: undefined,
})


onUpdated(async () => {
  useReservationSpendLimitsForPrivate.value = (adminEditingRestaurant.value?.brunchLunchMinSpendPerPerson == adminEditingRestaurant.value?.privateBrunchLunchMinSpendPerPerson && adminEditingRestaurant.value?.dinnerSunThruThursMinSpendPerPerson == adminEditingRestaurant.value?.privateDinnerSunThruThursMinSpendPerPerson && adminEditingRestaurant.value?.dinnerFriAndSatMinSpendPerPerson == adminEditingRestaurant.value?.privateDinnerFriAndSatMinSpendPerPerson)
})


const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const smsConfirmationMessage = `By clicking Yes below, you agree to receive automated text messages from RM to the phone number(s) you provided.
        These messages will contain information and links pertaining to reservation requests made by our members.
        We do not charge restaurants a fee, and consent is not a condition of purchase. Msg and data rates may apply. Msg frequency varies based on member demand.
        You may unsubscribe at any time by replying STOP to any text message, or by removing your phone number from the same form field where you provided it.
        Please see our <a href="/privacy" target="_blank">Privacy Policy</a> and <a href="/terms" target="_blank">Terms</a> for more information.`

const useReservationSpendLimitsForPrivate = ref(adminEditingRestaurant.value?.brunchLunchMinSpendPerPerson == adminEditingRestaurant.value?.privateBrunchLunchMinSpendPerPerson && adminEditingRestaurant.value?.dinnerSunThruThursMinSpendPerPerson == adminEditingRestaurant.value?.privateDinnerSunThruThursMinSpendPerPerson && adminEditingRestaurant.value?.dinnerFriAndSatMinSpendPerPerson == adminEditingRestaurant.value?.privateDinnerFriAndSatMinSpendPerPerson)

const setPrivateSpendLimitsToSameAsGeneral = () => {
  adminEditingRestaurant.value.privateBrunchLunchMinSpendPerPerson = adminEditingRestaurant.value.brunchLunchMinSpendPerPerson
  adminEditingRestaurant.value.privateDinnerSunThruThursMinSpendPerPerson = adminEditingRestaurant.value.dinnerSunThruThursMinSpendPerPerson
  adminEditingRestaurant.value.privateDinnerFriAndSatMinSpendPerPerson = adminEditingRestaurant.value.dinnerFriAndSatMinSpendPerPerson
}

const onlyRestaurantInMarket = computed(() => {
  let restaurantsInMarket = restaurants.value.filter(restaurant => restaurant.market == adminEditingRestaurant.value.market)
  
  return restaurantsInMarket.length == 1
})

const nameIsNotUnique = computed(() => {
  let duplicates = restaurants.value.filter(restaurant => restaurant.name == adminEditingRestaurant.value.name && restaurant._id != adminEditingRestaurant.value._id && restaurant.market == adminEditingRestaurant.value.market)

  return adminEditingRestaurant.value.name != "" && duplicates.length > 0
})

const exampleGeneralReservationUrl = computed(() => {
  if(adminEditingRestaurant?.value?.generalReservationUrl.toLowerCase().includes('opentable.com')){
    return `https://www.opentable.com/r/restaurant-city`
  }
  if(adminEditingRestaurant?.value?.generalReservationUrl.toLowerCase().includes('tock.com')){
    return `https://www.exploretock.com/restaurantname`
  }
  if(adminEditingRestaurant?.value?.generalReservationUrl.toLowerCase().includes('resy.com')){
    return `https://resy.com/cities/city/restaurant-name`
  }
  if(adminEditingRestaurant?.value?.generalReservationUrl.toLowerCase().includes('sevenrooms.com')){
    return `https://www.sevenrooms.com/reservations/restaurantname`
  }
  if(adminEditingRestaurant?.value?.generalReservationUrl.toLowerCase().includes('yelp.com')){
    return `https://www.yelp.com/reservations/restaurant-name`
  }
  
  return 'https://www.opentable.com/r/restaurant-city'
})

const onFormSubmit = async () => {
  form.value.validate().then(async ({valid: isValid}) => {
    if(isValid){
      // RM-189 — normalize all four append fields to clean [{ name, email|phone }]
      // arrays (drops blank rows; coerces any legacy comma-String) before saving.
      const r = adminEditingRestaurant.value
      const phonesChanged = normalizedAppends(r.generalReservationPhoneNumberAppends, 'phone') !== originalGeneralReservationPhoneNumberAppends
      r.generalReservationEmailAppends = cleanContactEntries(r.generalReservationEmailAppends, 'email')
      r.generalReservationPhoneNumberAppends = cleanContactEntries(r.generalReservationPhoneNumberAppends, 'phone')
      r.privateReservationEmailAppends = cleanContactEntries(r.privateReservationEmailAppends, 'email')
      r.instantBookingEmailAppends = cleanContactEntries(r.instantBookingEmailAppends, 'email')

      if(phonesChanged && r.generalReservationPhoneNumberAppends.length){
        isConfirmSmsDialogOpen.value = true
      }
      else{
        let wasNew = adminEditingRestaurant?.value?.isNew == true
        let saveErrors = await restaurantsStore.saveAdminEditingRestaurant()
        if(saveErrors){
          errors.value = Object.assign(errors.value, saveErrors)
        }
        else{
          if(wasNew){
            await userGroupsStore.getAllUserGroups()
          }
          emit('update:isDialogVisible', false)
        }
      }
    }
  })
}

const requestClose = () => {
  // isConfirmDialogOpen.value = true
  if (changesMade.value) {
    isConfirmDialogOpen.value = true
  }
  else{
    restaurantsStore.cancelAdminEditRestaurant()
    isDialogVisible.value = false
  }
}

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
restaurantsStore.$subscribe((mutation, state) => {
  isDialogVisible.value = ( state.adminEditingRestaurant != null)
})

//watcher for adminEditingRestaurant.value that sets changesMade true
watch(adminEditingRestaurant, (newValue, oldValue) => {
  if(!oldValue && newValue){
    changesMade.value = false
    // RM-157: collapse the Google Place panel when already linked.
    googlePlacePanel.value = newValue.googlePlaceId ? null : 0
  }else{
    changesMade.value = true
  }
}, {deep: true})
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogOpen"
    confirmation-msg="Save changes?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="restaurantsStore.saveAdminEditingRestaurant(); isDialogVisible = false;"
    @cancel="restaurantsStore.cancelAdminEditRestaurant(); isDialogVisible = false;"
  />
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmSmsDialogOpen"
    :confirmation-msg="smsConfirmationMessage"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="restaurantsStore.saveAdminEditingRestaurant(); isDialogVisible = false;"
  />
  <MediaChooserAndManager
    v-model:is-dialog-visible="choosingImages"
    title="Add/select pics"
    :preselected-media-ids="adminEditingRestaurant?.imageIds"
    multi-select
    :restaurant="adminEditingRestaurant"
    :crop="false"
    @media-selected="handleMediaSelection"
  />
  <VDialog
    v-if="adminEditingRestaurant && (myUser.userLevel == 'admin' || myUser?.primaryGroupPermission == 'admin')"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    fullscreen
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="width: 58px" />
      <VSpacer />
      <VToolbarTitle>Profile & Settings</VToolbarTitle>
      <VSpacer />
      <VToolbarItems>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          @click="requestClose"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbarItems>
    </VToolbar>
    <VCard class="pa-sm-9 pa-2 main-content">
      <VCardText>
        <!-- 👉 Form -->
        <VForm
          ref="form"
          v-model="valid"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <div class="mt-4">
              <b>General</b>
            </div>

            <!-- RM-157: Google Place link status + linker -->
            <VCol cols="12">
              <div class="d-flex align-center mb-2">
                <VIcon
                  :color="adminEditingRestaurant.googlePlaceId ? 'success' : 'grey'"
                  class="mr-2"
                >
                  {{ adminEditingRestaurant.googlePlaceId ? 'mdi-check-circle' : 'mdi-map-marker-off-outline' }}
                </VIcon>
                <span class="font-weight-medium">
                  {{ adminEditingRestaurant.googlePlaceId
                    ? `Linked to Google Place${(adminEditingRestaurant.googlePlaceDetails?.name || adminEditingRestaurant.nameSuggestion) ? `: ${adminEditingRestaurant.googlePlaceDetails?.name || adminEditingRestaurant.nameSuggestion}` : ''}`
                    : 'Not linked to a Google Place' }}
                </span>
              </div>
              <VExpansionPanels
                v-model="googlePlacePanel"
                variant="accordion"
                class="mb-4"
              >
                <VExpansionPanel>
                  <VExpansionPanelTitle>
                    <div class="d-flex align-center">
                      <VIcon class="mr-2">
                        mdi-google-maps
                      </VIcon>
                      <span class="font-weight-medium">
                        {{ adminEditingRestaurant.googlePlaceId ? 'Re-link Google Place' : 'Link a Google Place' }}
                      </span>
                    </div>
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <RestaurantSyncer
                      :restaurant-id="adminEditingRestaurant._id || adminEditingRestaurant.id"
                      :default-name="adminEditingRestaurant.name"
                      :default-city="adminEditingRestaurant.market"
                    />
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
            </VCol>

            <VCol cols="12">
              <div class="mb-2">
                <b>Photos</b>
              </div>
              <VCarousel
                v-model="currentImage"
                height="20dvh"
                hide-delimiters
                :show-arrows="adminEditingRestaurant.imageIds?.length > 1"
              >
                <template v-if="adminEditingRestaurant.imageIds?.length > 0">
                  <VCarouselItem
                    v-for="(photo, index) in adminEditingRestaurant.imageIds"
                    :key="index"
                    :src="mediaUrl(photo, 'small')"
                    :value="photo"
                    contain
                  />
                </template>
                <template v-else>
                  <VCarouselItem
                    :src="imagePlaceholder"
                    contain
                  />
                </template>
              </VCarousel>
              <div class="d-flex justify-center mt-2">
                <span
                  class="text-sm text-primary"
                  style="cursor: pointer;"
                  @click="choosingImages = true"
                >+ add/select pics</span>
              </div>
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.name"
                label="Name"
                autocomplete="null"
                required
                class="mb-2"
                :disabled="myUser.userLevel != 'admin' || nameIsNotUnique"
                :hint="`Name from Places: ${adminEditingRestaurant.nameSuggestion}`"
                :persistent-hint="!!adminEditingRestaurant.nameSuggestion"
              />
              <VAlert
                v-if="nameIsNotUnique"
                class="mt-4"
                type="warning"
              >
                This restaurant can't be renamed.
              </VAlert>
            </VCol>

            <VCol
              cols="12"
            >
              <VCombobox
                v-model="adminEditingRestaurant.market"
                class="mb-2"
                label="Market"
                :items="markets"
                :allow-custom-values="true"
                required
                :rules="[requiredValidator]"
                :disabled="myUser.userLevel != 'admin'"
                :hint="`Market from Places: ${adminEditingRestaurant.marketSuggestion}`"
                :persistent-hint="!!adminEditingRestaurant.marketSuggestion"
              />
              <VAlert
                v-if="onlyRestaurantInMarket"
                class="mt-4"
                type="warning"
              >
                First restaurant in this market.
              </VAlert>
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.marketSecondary"
                class="mb-2"
                label="Market (Secondary)"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VCombobox
                v-model="adminEditingRestaurant.timeZoneIdentifier"
                class="mb-2"
                label="Time Zone"
                :items="timeZones"
                :allow-custom-values="true"
                required
                :disabled="myUser.userLevel != 'admin'"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VCombobox
                v-model="adminEditingRestaurant.group"
                class="mb-2"
                label="Group"
                :items="groups"
                :allow-custom-values="true"
                required
                :disabled="myUser.userLevel != 'admin'"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VCombobox
                v-model="adminEditingRestaurant.secondaryGroup"
                class="mb-2"
                label="Secondary Group"
                :items="groups"
                :allow-custom-values="true"
                required
                :disabled="myUser.userLevel != 'admin'"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.cuisines"
                class="mb-2"
                label="Cuisines"
                :items="cuisineTags"
                multiple
                chips
                closable-chips
                :disabled="myUser.userLevel != 'admin'"
                hint="Auto-tagged by AI at creation — edit to correct. Drives cuisine-filtered discovery in the AI chat."
                persistent-hint
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VCombobox
                v-model="adminEditingRestaurant.neighborhoods"
                class="mb-2"
                label="Neighborhoods"
                :items="neighborhoodItems"
                multiple
                chips
                closable-chips
                :disabled="myUser.userLevel != 'admin'"
                hint="Auto-tagged by AI at creation — edit to correct (custom values allowed; boundary spots may carry two). Drives neighborhood-filtered discovery in the AI chat."
                persistent-hint
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingRestaurant.description"
                label="Overview"
                autocomplete="null"
                required
                class="mb-2"
              />
              <VAlert
                v-if="nameIsNotUnique"
                class="mt-4"
                type="warning"
              >
                This restaurant can't be renamed.
              </VAlert>
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.status"
                label="Status"
                item-title="text"
                item-value="value"
                :items="['Preferred', 'Member Favorite', 'Unlisted']"
                :rules="[requiredValidator]"
                required
                :disabled="myUser.userLevel != 'admin'"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.profileId"
                label="RM Profile"
                prefix="reserverm.com/r/"
                hint="lowercase letters, numbers and - only"
                :rules="[profileIdValidator]"
                :error-messages="errors.profileId"
                required
                counter="40"
                maxlength="40"
                @input="clearErrors"
              />
            </VCol>
            <VCol
              v-if="myUser.userLevel == 'admin'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.rmSortOrder"
                class="mt-2"
                label="RM Sort"
                hint="Higher values are shown further up the list"
                type="number"
              />
            </VCol>
            <VCol
              v-if="myUser.userLevel == 'admin'"
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.sourceType"
                label="Source"
                item-title="text"
                item-value="value"
                :items="[{text: 'Not Set', value: ''},{text: 'Member Favorite', value: 'Member Favorite'}, {text: 'Restaurant Added', value: 'Restaurant Added'}]"
                required
                class="mb-2"
              />
            </VCol>

            <!--            <VCol -->
            <!--              cols="12" -->
            <!--              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'" -->
            <!--            > -->
            <!--              <v-text-field -->
            <!--                v-model="adminEditingRestaurant.sourceEmail" -->
            <!--                label="Source Email" -->
            <!--                autocomplete="null" -->
            <!--                class="mb-2" -->
            <!--              /> -->
            <!--            </VCol> -->

            <!--            <VCol -->
            <!--                cols="12" -->
            <!--                v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'" -->
            <!--            > -->
            <!--              <v-text-field -->
            <!--                  v-model="adminEditingRestaurant.sourceCreditMessage" -->
            <!--                  label="Source Credit Message" -->
            <!--                  hint="Shown to members near restaurant to give them credit for suggesting" -->
            <!--                  autocomplete="null" -->
            <!--                  class="mb-2" -->
            <!--              /> -->
            <!--            </VCol> -->

            <VCol
              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.sourceReferredBy"
                label="Referred By"
                hint="Name of Person/organization to be listed as referral source"
                autocomplete="null"
                class="mb-2"
              />
            </VCol>

            <VCol
              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.sourceMemberProfileId"
                label="Referral Source Member Profile Id"
                hint="Profile id is the last part of the members profile URL, usually firstNameLastName"
                autocomplete="null"
                class="mb-2"
              />
            </VCol>


            <VCol
              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'"
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.sourceHasDined"
                label="Have you dined here?"
                :items="['Yes, love it', 'No, but want to']"
                clearable
                class="mb-2"
                hint="From member's add favorite request"
              />
            </VCol>

            <VCol
              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin' && adminEditingRestaurant.sourceHasDined === 'Yes, love it'"
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.sourceCloseWithMgmt"
                label="Are you close with mgmt team?"
                :items="['Yes & I can make introduction', 'No, not yet']"
                clearable
                class="mb-2"
                hint="From member's add favorite request"
              />
            </VCol>

            <VCol
              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin' && adminEditingRestaurant.sourceHasDined === 'No, but want to'"
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.sourceHowSoon"
                label="How soon?"
                :items="['Today', 'Tomorrow', 'This week', 'This month']"
                clearable
                class="mb-2"
                hint="From member's add favorite request"
              />
            </VCol>


            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.website"
                label="Website Url"
                autocomplete="null"
                required
                class="mb-2"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.address"
                label="Address"
                autocomplete="null"
                required
                class="mb-2"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.phoneNumber"
                label="Phone Number"
                autocomplete="null"
                required
                class="mb-2"
              />
            </VCol>

            <VCol cols="12">
              <VExpansionPanels
                variant="accordion"
                class="mb-4"
              >
                <VExpansionPanel>
                  <VExpansionPanelTitle>
                    <div class="d-flex align-center">
                      <VIcon class="mr-2">
                        mdi-clock-outline
                      </VIcon>
                      <span class="font-weight-medium">Opening Hours</span>
                    </div>
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <template v-if="adminEditingRestaurant.openingHours && adminEditingRestaurant.openingHours.length > 0">
                      <template
                        v-for="(dayHours, i) in adminEditingRestaurant.openingHours"
                        :key="i"
                      >
                        <VTextField
                          v-model="adminEditingRestaurant.openingHours[i]"
                          :label="moment().day(i+1).format('dddd')"
                          placeholder="e.g., 11:30 AM – 4:00 PM, 7:00 PM – 1:00 AM"
                          autocomplete="null"
                          density="comfortable"
                          variant="outlined"
                          class="mb-3"
                          hide-details
                        >
                          <template #prepend-inner>
                            <span class="text-caption text-grey mr-2">{{ moment().day(i+1).format('ddd') }}</span>
                          </template>
                        </VTextField>
                      </template>
                    </template>
                    <div
                      v-else
                      class="text-caption text-grey"
                    >
                      No opening hours set
                    </div>
                    <div class="text-caption text-grey mt-2">
                      Use format: 11:30 AM – 4:00 PM, 7:00 PM – 1:00 AM (for split hours)
                    </div>
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
            </VCol>

            <!--            <VCol -->
            <!--              cols="12" -->
            <!--              v-if="adminEditingRestaurant.sourceType && myUser.userLevel == 'admin'" -->
            <!--              class="mb-2" -->
            <!--            > -->
            <!--              <span v-if="adminEditingRestaurant.sourceMessage">Message: {{adminEditingRestaurant.sourceMessage}}<br></span> -->
            <!--              <span v-if="adminEditingRestaurant.sourceTimestamp">Submitted: {{moment(adminEditingRestaurant.sourceTimestamp).format("M/D/YY [at] H:MMa")}}</span> -->
            <!--            </VCol> -->

            <div><b>Account Owner:</b></div>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingRestaurant.ownerName"
                label="Name"
                autocomplete="null"
                hint=""
                class="mb-2"
                rows="1"
                auto-grow
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingRestaurant.ownerTitle"
                label="Position/Title"
                autocomplete="null"
                hint=""
                class="mb-2"
                rows="1"
                auto-grow
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingRestaurant.ownerEmail"
                label="Work Email"
                autocomplete="null"
                hint=""
                class="mb-2"
                rows="1"
                auto-grow
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingRestaurant.ownerRestaurantGroup"
                label="Restaurant Group (if applicable)"
                autocomplete="null"
                hint=""
                class="mb-2"
                rows="1"
                auto-grow
              />
            </VCol>

            <div><b>Team Information:</b></div>
            <VCol
              cols="12"
            >
              <VCheckbox
                v-model="adminEditingRestaurant.acceptingReferrals"
                label="Accepting Referrals"
              />
              <VCheckbox
                v-model="adminEditingRestaurant.contactInfoNotVerified"
                label="Contact Info Not Verified"
                hint="Check if the restaurant's contact information has not been verified"
                persistent-hint
              />
            </VCol>
            <!-- RM-359: whether RM reviews requests before they're sent here, per member category -->
            <VCol
              v-if="myUser?.userLevel === 'admin'"
              cols="12"
              class="review-settings"
            >
              <div class="review-settings__title">
                RM reviews requests from
              </div>
              <div class="review-settings__switches">
                <VSwitch
                  v-for="c in REVIEW_CATEGORIES"
                  :key="c.key"
                  :model-value="reviewOn(c.key)"
                  :label="c.label"
                  color="primary"
                  density="compact"
                  hide-details
                  inset
                  @update:model-value="v => setReview(c.key, v)"
                />
              </div>
              <div class="review-settings__hint">
                {{ reviewDefaultsHint }}. Internal to RM.
                <button
                  v-if="reviewSettingsInForce"
                  type="button"
                  class="review-settings__reset"
                  @click="resetReview"
                >
                  Reset to default
                </button>
              </div>
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.generalReservationTeamIdentity"
                label="Team Identity for Reservation Referrals"
                autocomplete="null"
                :hint="`e.g. ${adminEditingRestaurant.name} Reservations Team`"
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Emails to receive Reservation Referrals
              </div>
              <ContactAppendsEditor
                v-model="adminEditingRestaurant.generalReservationEmailAppends"
                value-key="email"
                value-label="Email"
                value-type="email"
                add-label="Add email"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Phone Numbers to receive Reservation Referrals
              </div>
              <ContactAppendsEditor
                v-model="adminEditingRestaurant.generalReservationPhoneNumberAppends"
                value-key="phone"
                value-label="Phone number"
                value-type="tel"
                add-label="Add phone number"
                hint="(312) 399-5527 format"
                toggle-key="automatedTexts"
                toggle-label="Receive automated texts"
                :captions="teamCaptions"
              />
              <div class="text-caption text-medium-emphasis mt-1">
                Automated texts go out the moment a member submits a request, to each checked number whose person has confirmed texts from RM (RM via Text → Restaurant team).
              </div>
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.privateReservationTeamIdentity"
                label="Team Identity for Private Event Referrals"
                autocomplete="null"
                :hint="`e.g. ${adminEditingRestaurant.name} Private Events Team`"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Emails to receive Private Event Requests
              </div>
              <ContactAppendsEditor
                v-model="adminEditingRestaurant.privateReservationEmailAppends"
                value-key="email"
                value-label="Email"
                value-type="email"
                add-label="Add email"
              />
            </VCol>

            <div><b>Instant Booking (Platinum Reserved) Team:</b></div>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.instantBookingTeamIdentity"
                label="Team Identity for Instant Bookings"
                autocomplete="null"
                :hint="`e.g. ${adminEditingRestaurant.name} Platinum Reservations Team`"
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Emails to receive Instant Bookings
              </div>
              <ContactAppendsEditor
                v-model="adminEditingRestaurant.instantBookingEmailAppends"
                value-key="email"
                value-label="Email"
                value-type="email"
                add-label="Add email"
                hint="Receives booking confirmations, cancellations, and follow-up emails"
              />
            </VCol>

            <div><b>General Details:</b></div>

            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.generalReservationPlatform"
                label="Primary Reservation Platform"
                item-title="text"
                item-value="value"
                :items="[{text: 'Not Set', value: ''},{text: 'OpenTable', value: 'OpenTable'}, {text: 'SevenRooms', value: 'SevenRooms'}, {text: 'Resy', value: 'Resy'}, {text: 'Tock', value: 'Tock'}, {text: 'Yelp', value: 'Yelp'}, {text: 'Other', value: 'Other'}]"
                hint="The system used to manage reservations."
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingRestaurant.generalReservationUrl"
                label="Base Reservations Url"
                autocomplete="null"
                :rules="[reservationPlatformUrlValidator]"
                :hint="`e.g. ${exampleGeneralReservationUrl}`"
                class="mb-2"
              />
              <div
                v-if="generateGeneralReservationUrlWithQueryString(adminEditingRestaurant)"
                class="text-caption"
              >
                Test Url: <a
                  :href="generateGeneralReservationUrlWithQueryString(adminEditingRestaurant)"
                  target="_blank"
                >{{ generateGeneralReservationUrlWithQueryString(adminEditingRestaurant) }}</a>
              </div>
              <!-- RM-403: links on other platforms the website reader found, and a read of the website on request. -->
              <div
                v-if="(adminEditingRestaurant.additionalReservationLinks || []).length"
                class="text-caption mt-2"
              >
                Also takes reservations on:
                <template
                  v-for="(link, i) in adminEditingRestaurant.additionalReservationLinks"
                  :key="link.url"
                >
                  <template v-if="i">
                    ,
                  </template>
                  <a
                    :href="link.url"
                    target="_blank"
                    rel="noopener"
                  >{{ link.platform || 'Other' }}</a>
                </template>
              </div>
              <div
                v-if="adminEditingRestaurant._id"
                class="mt-2"
              >
                <VBtn
                  variant="text"
                  color="primary"
                  size="small"
                  :loading="websiteReadBusy"
                  :disabled="websiteReadBusy"
                  @click="readWebsiteNow"
                >
                  Read website now
                </VBtn>
                <span
                  v-if="websiteReadNote"
                  class="text-caption ml-2"
                >{{ websiteReadNote }}</span>
              </div>
            </VCol>

            <VCol
              cols="12"
            >
              <VCheckbox
                v-model="adminEditingRestaurant.generalReservationHoldRequired"
                label="Require Credit Card Hold"
              />
              <VSelect
                v-if="adminEditingRestaurant.generalReservationHoldRequired"
                v-model="adminEditingRestaurant.generalReservationHoldType"
                label="Hold Type"
                item-title="text"
                item-value="value"
                :items="[{text:'Full F&B Minimum+tax+tip', value: 'full'} ,{text:'Full+Charge', value: 'fullWithCharge'} ,{text:'$25 per person', value: '25pp'} ,{text:'$50 per person', value: '50pp'}]"
                :rules="[requiredValidator]"
                required
                hint="How much should be held on members' credit cards?"
                class="mt-4"
              />
              <VTextField
                v-if="adminEditingRestaurant.generalReservationHoldRequired"
                v-model="adminEditingRestaurant.generalReservationHoldTaxPercentage"
                class="mt-2"
                label="Sales Tax Rate"
                hint="The combined applicable state and local sales/use tax to be added to the credit card hold."
                type="number"
                suffix="%"
              />

              <VSelect
                v-if="adminEditingRestaurant.generalReservationHoldRequired"
                v-model="adminEditingRestaurant.generalReservationCCFeePercentage"
                label="Members Credit Card Transaction Fee"
                hint="You will be charged a ~3% credit card transaction fee. Here you can pass all/part of that fee on to the member."
                item-title="text"
                item-value="value"
                :items="[{text:'None', value: 0},{text:'1.5%', value: 1.5},{text:'2%', value: 2},{text:'2.5%', value: 2.5},{text:'3%', value: 3}]"
                :rules="[requiredValidator]"
                required
                class="mt-4"
              />
              <VSelect
                v-if="adminEditingRestaurant.generalReservationHoldRequired"
                v-model="adminEditingRestaurant.generalReservationHoldCancellationLimitHours"
                label="Cancellation Notice Period"
                item-title="text"
                item-value="value"
                :items="[{text:'4 hours', value: 4} ,{text:'8 hours', value: 8} ,{text:'12 hours', value: 12} ,{text:'24 hours', value: 24} ,{text:'48 hours', value: 48}]"
                :rules="[requiredValidator]"
                required
                hint="If a reservation is canceled before this period, the credit card hold is fully released."
                class="mt-4"
              />

              <VCheckbox
                v-if="!adminEditingRestaurant.generalReservationHoldRequired"
                v-model="adminEditingRestaurant.generalReservationAllowCancellation"
                label="Allow Member to Cancel reservations via RM"
              />

              <VTextField
                v-model.number="adminEditingRestaurant.reservationBookingWindowDays"
                label="Booking window (days members can request out)"
                type="number"
                min="1"
                placeholder="No limit"
                :hint="bookingWindowGuidance"
                persistent-hint
                class="mt-4"
                style="font-size: 16px;"
              />
            </VCol>

            <div><b>Minimum Spends (Reservations)</b></div>

            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.brunchLunchMinSpendPerPerson"
                label="Min Spend Per Person 3pm or earlier"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.dinnerSunThruThursMinSpendPerPerson"
                label="Min Spend Per Person after 3pm (Sun-Thurs)"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.dinnerFriAndSatMinSpendPerPerson"
                label="Min Spend Per Person after 3pm (Fri & Sat)"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingRestaurant.maxGeneralReservationPartySize"
                label="Maximum party size for general dining"
                item-title="text"
                item-value="value"
                :items="[1,2,3,4,5,6,7,8,9,10,11,12,13,14]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests general dining requests at or below this number."
              />
            </VCol>

            <div><b>Minimum Spends (Private/Group)</b></div>
            <VCol
              cols="12"
            >
              <VCheckbox
                v-model="useReservationSpendLimitsForPrivate"
                label="Use above Reservation minimum spends for Events"
                class="mb-2"
                @change="newValue => newValue && setPrivateSpendLimitsToSameAsGeneral()"
              />
              <VSelect
                v-if="!useReservationSpendLimitsForPrivate"
                v-model="adminEditingRestaurant.privateBrunchLunchMinSpendPerPerson"
                label="Min Spend Per Person 3pm or earlier"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-if="!useReservationSpendLimitsForPrivate"
                v-model="adminEditingRestaurant.privateDinnerSunThruThursMinSpendPerPerson"
                label="Min Spend Per Person after 3pm (Sun-Thurs)"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-if="!useReservationSpendLimitsForPrivate"
                v-model="adminEditingRestaurant.privateDinnerFriAndSatMinSpendPerPerson"
                label="Min Spend Per Person after 3pm (Fri & Sat)"
                item-title="text"
                item-value="value"
                :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                :rules="[requiredValidator]"
                required
                hint="The restaurant will only receive requests at or above this amount."
                class="mb-2"
              />
            </VCol>

            <div><b>Misc</b></div>
            <VCol
              cols="12"
            >
              <VCheckbox
                v-model="adminEditingRestaurant.showHappeningsIcon"
                label="Show Happenings Icon"
                class="mb-2"
              />
            </VCol>
            <!-- 👉 Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                v-if="true"
                @click="form?.validate(); onFormSubmit()"
              >
                Save
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-selection-control-group {
    display: flex;
    flex-direction: unset;
  }

  .v-toolbar-title{
    flex: unset;
  }

  .toolbar-header{
    position: fixed;
    z-index: 1000;
  }

  .main-content{
    top: 60px;
    position: relative;
    padding-bottom: 60px !important;
  }

  .review-settings__title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .review-settings__switches {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .review-settings__hint {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    margin-top: 4px;
  }

  .review-settings__reset {
    margin-left: 8px;
    padding: 0;
    border: 0;
    background: none;
    color: rgb(var(--v-theme-primary));
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
  }
</style>

